import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'
import { QualificationRuleField } from '@/types/qualification-eligibility.types'
import type { EligibilityReason } from '@/types/qualification-eligibility.types'
import type { DocumentTypeCatalogItem, SealCatalogItem } from '@/types/professional-documents.types'

export const CATALOG_LOAD_ERROR = 'Não foi possível carregar o catálogo de documentos'

/** Catálogo como dado (D8): nomes por chave para fila, Requisitos e "o que falta". */
export const useProfessionalDocumentCatalogStore = defineStore(
  'professionalDocumentCatalog',
  () => {
    const types = ref<DocumentTypeCatalogItem[]>([])
    const seals = ref<SealCatalogItem[]>([])
    const isLoading = ref(false)
    const isLoaded = ref(false)
    /** Backend com a flag desligada (404 `PROFESSIONAL_DOCUMENTS_DISABLED`). */
    const isUnavailable = ref(false)
    const loadError = ref<string | null>(null)
    const hasError = computed(() => loadError.value !== null)
    let inFlight: Promise<void> | null = null

    async function load(): Promise<void> {
      isLoading.value = true
      loadError.value = null
      try {
        const data = await Network.professionalDocuments.getCatalog()
        types.value = data.types
        seals.value = data.seals
        isLoaded.value = true
      } catch (error) {
        if (isProfessionalDocumentsDisabled(error)) {
          // fallback intencional: a feature está desligada no backend; `isUnavailable` é o
          // estado que a tela mostra ("indisponível"), não é erro nem toast.
          isUnavailable.value = true
          return
        }
        console.error(error)
        loadError.value = getApiErrorMessage(error, CATALOG_LOAD_ERROR)
        throw error
      } finally {
        isLoading.value = false
        inFlight = null
      }
    }

    function ensureLoaded(): Promise<void> {
      if (isLoaded.value || isUnavailable.value) return Promise.resolve()
      inFlight ??= load()
      return inFlight
    }

    const typeName = (key: string): string => types.value.find((t) => t.key === key)?.name ?? key
    const sealName = (key: string): string => seals.value.find((s) => s.key === key)?.name ?? key
    const reasonName = (reason: Pick<EligibilityReason, 'field' | 'key'>): string =>
      reason.field === QualificationRuleField.SEAL ? sealName(reason.key) : typeName(reason.key)

    function $reset() {
      types.value = []
      seals.value = []
      isLoading.value = false
      isLoaded.value = false
      isUnavailable.value = false
      loadError.value = null
      inFlight = null
    }

    return {
      types,
      seals,
      isLoading,
      isUnavailable,
      loadError,
      hasError,
      ensureLoaded,
      typeName,
      sealName,
      reasonName,
      $reset
    }
  }
)
